import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const AdminPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditSettings, setShowEditSettings] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // New user form state
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    isActive: true
  });

  // Settings form state
  const [userSettings, setUserSettings] = useState({
    webhookUrl: '',
    elevenLabsVoiceId: '',
    sttLang: 'en-US',
    elevenLabsApiKey: ''
  });
  const [maskedApiKey, setMaskedApiKey] = useState('');

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user, currentPage, searchQuery]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/v2/admin/users?query=${searchQuery}&page=${currentPage}&size=10`);
      setUsers(response.data.users);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/v2/admin/users', newUser);
      if (response.data.success) {
        setShowAddUser(false);
        setNewUser({
          name: '',
          email: '',
          password: '',
          role: 'user',
          isActive: true
        });
        fetchUsers();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create user');
    }
  };

  const updateUserSettings = async (e) => {
    e.preventDefault();
    try {
      // Build a payload that omits empty secrets
      const payload = {
        webhookUrl: userSettings.webhookUrl?.trim(),
        elevenLabsVoiceId: userSettings.elevenLabsVoiceId?.trim(),
        sttLang: userSettings.sttLang
      };
      
      // Only include API key if provided and non-empty after trim
      if (userSettings.elevenLabsApiKey?.trim()) {
        payload.elevenLabsApiKey = userSettings.elevenLabsApiKey.trim();
      }

      const response = await api.put(
        `/v2/admin/users/${selectedUser._id}/settings`,
        payload
      );
      if (response.data.success) {
        setShowEditSettings(false);
        setSelectedUser(null);
        setUserSettings({
          webhookUrl: '',
          elevenLabsVoiceId: '',
          sttLang: 'en-US',
          elevenLabsApiKey: ''
        });
        setMaskedApiKey('');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update settings');
    }
  };

  const deactivateUser = async (userId) => {
    if (window.confirm('Are you sure you want to deactivate this user?')) {
      try {
        await api.delete(`/v2/admin/users/${userId}`);
        fetchUsers();
      } catch (error) {
        setError('Failed to deactivate user');
      }
    }
  };

  const openEditSettings = async (user) => {
    setSelectedUser(user);
    try {
      const response = await api.get(`/v2/admin/users/${user._id}/settings`);
      if (response.data.success) {
        // Set form data with the actual API key for admin editing
        setUserSettings({
          webhookUrl: response.data.settings.webhookUrl || '',
          elevenLabsVoiceId: response.data.settings.elevenLabsVoiceId || '',
          sttLang: response.data.settings.sttLang || 'en-US',
          elevenLabsApiKey: response.data.settings.elevenLabsApiKey || '' // Use actual API key for admin
        });
        setMaskedApiKey(response.data.settings.elevenLabsApiKeyMasked || '');
        setShowEditSettings(true);
      }
    } catch (error) {
      setError('Failed to load user settings');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold text-red-400">Access Denied</h2>
        <p className="text-gray-400">You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">User Management</h1>
        <button
          onClick={() => setShowAddUser(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          Add User
        </button>
      </div>

      {error && (
        <div className="bg-red-500 text-white p-3 rounded-lg mb-4">
          {error}
          <button onClick={() => setError('')} className="float-right">×</button>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        />
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="text-center py-8">Loading users...</div>
      ) : (
        <>
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-700">
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Role</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Last Updated</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id} className="border-b border-gray-700 hover:bg-gray-750">
                    <td className="p-3">{user.name}</td>
                    <td className="p-3">{user.email}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.role === 'admin' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.isActive 
                          ? 'bg-green-600 text-white' 
                          : 'bg-red-600 text-white'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3">
                      {new Date(user.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="p-3 space-x-2">
                      <button
                        onClick={() => openEditSettings(user)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                      >
                        Settings
                      </button>
                      <button
                        onClick={() => deactivateUser(user._id)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-4 space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">Add New User</h2>
            <form onSubmit={createUser}>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className="px-4 py-2 bg-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Settings Modal */}
      {showEditSettings && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-96 max-h-96 overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Settings for {selectedUser.name}
            </h2>
            <form onSubmit={updateUserSettings}>
              <div className="space-y-3">
                <input
                  type="url"
                  placeholder="Webhook URL (HTTPS only"
                  value={userSettings.webhookUrl}
                  onChange={(e) => setUserSettings({...userSettings, webhookUrl: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
                <input
                  type="text"
                  placeholder="ElevenLabs Voice ID"
                  value={userSettings.elevenLabsVoiceId}
                  onChange={(e) => setUserSettings({...userSettings, elevenLabsVoiceId: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
                <select
                  value={userSettings.sttLang}
                  onChange={(e) => setUserSettings({...userSettings, sttLang: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="hi-IN">Hindi (India)</option>
                  <option value="en-US">English (US)</option>
                  <option value="en-IN">English (India)</option>
                  <option value="en-GB">English (UK)</option>
                  <option value="es-ES">Spanish (Spain)</option>
                  <option value="fr-FR">French (France)</option>
                  <option value="de-DE">German (Germany)</option>
                  <option value="it-IT">Italian (Italy)</option>
                  <option value="pt-BR">Portuguese (Brazil)</option>
                  <option value="ru-RU">Russian (Russia)</option>
                  <option value="ja-JP">Japanese (Japan)</option>
                  <option value="ko-KR">Korean (Korea)</option>
                  <option value="zh-CN">Chinese (Simplified)</option>
                  <option value="zh-TW">Chinese (Traditional)</option>
                </select>
                <input
                  type="password"
                  placeholder="ElevenLabs API Key"
                  value={userSettings.elevenLabsApiKey}
                  onChange={(e) => setUserSettings({...userSettings, elevenLabsApiKey: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
                {maskedApiKey && (
                  <div className="text-sm text-gray-400">
                    Current API Key: {maskedApiKey}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowEditSettings(false)}
                  className="px-4 py-2 bg-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Save Settings
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
